// dog-management.jsx - 犬種グループ・犬種管理（TanStack Query版）
import React, { useMemo, useState } from 'react';
import axios from 'axios';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import './dogmanagement.css';
import useNotificationStore from '../stores/notificationStore'; // 通知用

/* ========================================
 * 定数
 * ======================================== */
const API_URL = import.meta.env.VITE_API_URL;

// ページあたりの表示件数オプション
const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

/* ========================================
 * ヘルパー関数
 * ======================================== */
const pad2 = (n) => String(n).padStart(2, '0');

const formatDateTime = (dateStr) => {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  if (isNaN(d)) return '—';
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())} ${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
};

/* ========================================
 * モーダルコンポーネント：犬種グループ追加/編集
 * ======================================== */
function DogGroupFormModal({ isOpen, onClose, mutation, dogGroup }) {
  const isEdit = !!dogGroup;
  const [formData, setFormData] = useState({
    group_name: '',
  });

  const [errors, setErrors] = useState({});

  // データが変わったらフォームを初期化
  React.useEffect(() => {
    if (dogGroup) {
      setFormData({
        group_name: dogGroup.group_name || '',
      });
    } else {
      setFormData({
        group_name: '',
      });
    }
    setErrors({});
  }, [dogGroup, isOpen]);

  if (!isOpen) return null;

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value,
    }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.group_name.trim()) newErrors.group_name = 'グループ名は必須です';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    const submitData = {
      group_name: formData.group_name,
    };

    if (isEdit) {
      mutation.mutate({ id: dogGroup.id, data: submitData, type: 'group' });
    } else {
      mutation.mutate({ data: submitData, type: 'group', isNew: true });
    }
  };

  return (
    <div className="dmModalOverlay" onMouseDown={onClose}>
      <div className="dmModalContent dmModalSmall" onMouseDown={(e) => e.stopPropagation()}>
        <div className="dmModalHeader">
          <h2 className="dmModalTitle">
            {isEdit ? '犬種グループを編集' : '犬種グループを追加'}
          </h2>
          <button type="button" className="dmModalClose" onClick={onClose} aria-label="閉じる">
            ✕
          </button>
        </div>

        <form onSubmit={handleSubmit} className="dmModalBody">
          <div className="dmFormGroup">
            <label htmlFor="group_name" className="dmFormLabel">
              グループ名 <span className="dmRequired">*</span>
            </label>
            <input
              id="group_name"
              name="group_name"
              className={`dmFormInput ${errors.group_name ? 'is-error' : ''}`}
              value={formData.group_name}
              onChange={handleChange}
              placeholder="例）牧羊犬・牧畜犬"
            />
            {errors.group_name && <span className="dmErrorText">{errors.group_name}</span>}
          </div>

          <div className="dmModalFooter">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              キャンセル
            </button>
            <button type="submit" className="btn btn-primary" disabled={mutation.isPending}>
              {mutation.isPending ? '保存中...' : isEdit ? '更新する' : '追加する'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ========================================
 * モーダルコンポーネント：犬種グループ一覧（編集機能付き）
 * ======================================== */
function DogGroupListModal({ isOpen, onClose, dogGroups, onEdit, onAdd }) {
  if (!isOpen) return null;

  return (
    <div className="dmModalOverlay" onMouseDown={onClose}>
      <div className="dmModalContent" onMouseDown={(e) => e.stopPropagation()}>
        <div className="dmModalHeader">
          <h2 className="dmModalTitle">犬種グループ一覧</h2>
          <button type="button" className="dmModalClose" onClick={onClose} aria-label="閉じる">
            ✕
          </button>
        </div>

        <div className="dmModalBody">
          <div className="dmGroupList">
            {dogGroups.length === 0 ? (
              <p className="dmGroupList-empty">犬種グループがありません</p>
            ) : (
              <table className="dmGroupList-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>グループ名</th>
                    <th>操作</th>
                  </tr>
                </thead>
                <tbody>
                  {dogGroups.map((group) => (
                    <tr key={group.id}>
                      <td className="dmGroupList-id">{group.id}</td>
                      <td className="dmGroupList-name">{group.group_name}</td>
                      <td className="dmGroupList-actions">
                        <button
                          className="btn btn-primary btn-sm"
                          onClick={() => onEdit(group)}
                        >
                          ✏️編集
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        <div className="dmModalFooter">
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            閉じる
          </button>
          <button type="button" className="btn btn-primary" onClick={onAdd}>
            ＋ グループ追加
          </button>
        </div>
      </div>
    </div>
  );
}

/* ========================================
 * モーダルコンポーネント：犬種・バラエティー追加/編集
 * ======================================== */
function DogBreedFormModal({ isOpen, onClose, mutation, dogBreed, dogGroups }) {
  const isEdit = !!dogBreed;
  const [formData, setFormData] = useState({
    group_id: '',
    breed_name: '',
    variety_name: '',
    is_visible: true,
  });

  const [errors, setErrors] = useState({});

  // データが変わったらフォームを初期化
  React.useEffect(() => {
    if (dogBreed) {
      setFormData({
        group_id: dogBreed.group_id || '',
        breed_name: dogBreed.breed_name || '',
        variety_name: dogBreed.variety_name || '',
        is_visible: dogBreed.is_visible ?? true,
      });
    } else {
      setFormData({
        group_id: dogGroups?.[0]?.id || '',
        breed_name: '',
        variety_name: '',
        is_visible: true,
      });
    }
    setErrors({});
  }, [dogBreed, isOpen, dogGroups]);

  if (!isOpen) return null;

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value,
    }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.group_id) newErrors.group_id = '犬種グループは必須です';
    if (!formData.breed_name.trim()) newErrors.breed_name = '犬種名は必須です';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    const submitData = {
      group_id: Number(formData.group_id),
      breed_name: formData.breed_name,
      variety_name: formData.variety_name,
      is_visible: formData.is_visible,
    };

    if (isEdit) {
      mutation.mutate({ id: dogBreed.id, data: submitData, type: 'breed' });
    } else {
      mutation.mutate({ data: submitData, type: 'breed', isNew: true });
    }
  };

  return (
    <div className="dmModalOverlay" onMouseDown={onClose}>
      <div className="dmModalContent" onMouseDown={(e) => e.stopPropagation()}>
        <div className="dmModalHeader">
          <h2 className="dmModalTitle">
            {isEdit ? '犬種・バラエティーを編集' : '犬種・バラエティーを追加'}
          </h2>
          <button type="button" className="dmModalClose" onClick={onClose} aria-label="閉じる">
            ✕
          </button>
        </div>

        <form onSubmit={handleSubmit} className="dmModalBody">
          <div className="dmFormGrid">
            <div className="dmFormGroup dmFormGroup-full">
              <label htmlFor="group_id" className="dmFormLabel">
                犬種グループ <span className="dmRequired">*</span>
              </label>
              <div className="dmFormSelectWrapper">
                <select
                  id="group_id"
                  name="group_id"
                  className={`dmFormSelect ${errors.group_id ? 'is-error' : ''}`}
                  value={formData.group_id}
                  onChange={handleChange}
                >
                  <option value="">選択してください</option>
                  {dogGroups?.map((group) => (
                    <option key={group.id} value={group.id}>
                      {group.group_name}
                    </option>
                  ))}
                </select>
              </div>
              {errors.group_id && <span className="dmErrorText">{errors.group_id}</span>}
            </div>

            <div className="dmFormGroup dmFormGroup-full">
              <label htmlFor="breed_name" className="dmFormLabel">
                犬種名 <span className="dmRequired">*</span>
              </label>
              <input
                id="breed_name"
                name="breed_name"
                className={`dmFormInput ${errors.breed_name ? 'is-error' : ''}`}
                value={formData.breed_name}
                onChange={handleChange}
                placeholder="例）ウェルシュ・コーギー・カーディガン"
              />
              {errors.breed_name && <span className="dmErrorText">{errors.breed_name}</span>}
            </div>

            <div className="dmFormGroup dmFormGroup-full">
              <label htmlFor="variety_name" className="dmFormLabel">
                バラエティー名
              </label>
              <input
                id="variety_name"
                name="variety_name"
                className="dmFormInput"
                value={formData.variety_name}
                onChange={handleChange}
                placeholder="例）ロング、スムース など（任意）"
              />
              <span className="dmFormHint">※バラエティーがある場合のみ入力してください</span>
            </div>
            
            <div className="dmFormGroup dmFormGroup-full">
                <label className="dmFormLabel">表示設定</label>
                <label className="dmCheckboxLabel">
                    <input
                    type="checkbox"
                    name="is_visible"
                    checked={formData.is_visible}
                    onChange={handleChange}
                    />
                    <span className="dmCheckboxText">一覧に表示する</span>
                </label>
            </div>

          </div>

          <div className="dmModalFooter">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              キャンセル
            </button>
            <button type="submit" className="btn btn-primary" disabled={mutation.isPending}>
              {mutation.isPending ? '保存中...' : isEdit ? '更新する' : '追加する'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ========================================
 * モーダルコンポーネント：確認
 * ======================================== */
function ConfirmModal({ isOpen, onClose, onConfirm, title, message, targetName, warning, confirmText, confirmButtonClass = 'btn-danger' }) {
  if (!isOpen) return null;

  return (
    <div className="dmModalOverlay" onMouseDown={onClose}>
      <div className="dmModalContent dmModalSmall" onMouseDown={(e) => e.stopPropagation()}>
        <div className="dmModalHeader">
          <h2 className="dmModalTitle">{title}</h2>
          <button type="button" className="dmModalClose" onClick={onClose} aria-label="閉じる">
            ✕
          </button>
        </div>

        <div className="dmModalBody">
          <p className="dmConfirmMessage">{message}</p>
          {targetName && (
            <p className="dmConfirmTarget"><strong>{targetName}</strong></p>
          )}
          {warning && <p className="dmConfirmWarning">{warning}</p>}
        </div>

        <div className="dmModalFooter">
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            キャンセル
          </button>
          <button type="button" className={`btn ${confirmButtonClass}`} onClick={onConfirm}>
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ========================================
 * メインコンポーネント：犬種グループ・犬種管理
 * ======================================== */
export default function DogManagement() {
  const queryClient = useQueryClient();

  // フィルター用
  const [searchQuery, setSearchQuery] = useState('');
  const [groupFilter, setGroupFilter] = useState('');
  const [visibilityFilter, setVisibilityFilter] = useState('');

  // ページネーション用
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);

  // モーダル制御
  const [isGroupFormOpen, setIsGroupFormOpen] = useState(false);
  const [isGroupListOpen, setIsGroupListOpen] = useState(false);
  const [isBreedFormOpen, setIsBreedFormOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [editingBreed, setEditingBreed] = useState(null);

  // 確認モーダル用
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    targetName: '',
    warning: '',
    confirmText: '',
    confirmButtonClass: 'btn-danger',
    onConfirm: null,
  });

  // 通知メッセージ
  const { notification, showNotification } = useNotificationStore();

  /* ===== データ取得（useQuery） ===== */
  // 犬種グループ一覧
  const { data: groupsData } = useQuery({
    queryKey: ['dogGroups'],
    queryFn: async () => {
      const res = await axios.get(`${API_URL}/api/admin/dog_groups/`);
      if (!res.data?.success) {
        throw new Error('犬種グループの取得に失敗しました');
      }
      return res.data.items || [];
    },
  });

  // 犬種・バラエティー一覧
  const { data: breedsData, isLoading, isError, error } = useQuery({
    queryKey: ['dogBreeds'],
    queryFn: async () => {
      const res = await axios.get(`${API_URL}/api/admin/dog_breeds/`);
      if (!res.data?.success) {
        throw new Error('犬種一覧の取得に失敗しました');
      }
      return res.data.items || [];
    },
  });

  const dogGroups = groupsData || [];
  const dogBreeds = breedsData || [];

  /* ===== 保存処理（useMutation） ===== */
  const saveMutation = useMutation({
    mutationFn: async ({ id, data, type, isNew }) => {
      let res;
      if (type === 'group') {
        if (isNew) {
          res = await axios.post(`${API_URL}/api/admin/dog_groups/`, data);
        } else {
          res = await axios.patch(`${API_URL}/api/admin/dog_groups/${id}/`, data);
        }
      } else {
        if (isNew) {
          res = await axios.post(`${API_URL}/api/admin/dog_breeds/`, data);
        } else {
          res = await axios.patch(`${API_URL}/api/admin/dog_breeds/${id}/`, data);
        }
      }
      if (!res.data?.success) {
        throw new Error(res.data?.message || '保存に失敗しました');
      }
      return { type, isNew };
    },
    onSuccess: ({ type, isNew }) => {
      const action = isNew ? '追加' : '更新';
      const target = type === 'group' ? '犬種グループ' : '犬種';
      showNotification(`${target}を${action}しました`);
      queryClient.invalidateQueries({ queryKey: ['dogGroups'] });
      queryClient.invalidateQueries({ queryKey: ['dogBreeds'] });
      
      if (isNew) {
      setIsGroupFormOpen(false);
      setIsBreedFormOpen(false);
      setEditingGroup(null);
      setEditingBreed(null);
      }  
      
    },
    onError: (err) => {
      showNotification(err.message || '保存に失敗しました', 'error');
    },
  });

  /* ===== フィルタリング ===== */
  const filteredBreeds = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();

    return dogBreeds.filter((breed) => {
      // グループ情報を取得
      const group = dogGroups.find((g) => g.id === breed.group_id);
      const groupLabel = group ? group.group_name : '';

      const matchesQuery =
        !q ||
        (breed.breed_name || '').toLowerCase().includes(q) ||
        (breed.variety_name || '').toLowerCase().includes(q) ||
        groupLabel.toLowerCase().includes(q);

      // 型を合わせて比較
      const matchesGroup = !groupFilter || breed.group_id === Number(groupFilter);

      const matchesVisibility =
        !visibilityFilter ||
        (visibilityFilter === 'visible' && breed.is_visible) ||
        (visibilityFilter === 'hidden' && !breed.is_visible);

      return matchesQuery && matchesGroup && matchesVisibility;
    });
  }, [dogBreeds, dogGroups, searchQuery, groupFilter, visibilityFilter]);

  /* ===== ページネーション計算 ===== */
  const totalItems = filteredBreeds.length;
  const totalPages = Math.ceil(totalItems / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = Math.min(startIndex + pageSize, totalItems);
  const paginatedBreeds = filteredBreeds.slice(startIndex, endIndex);

  // ページサイズ変更時にページをリセット
  const handlePageSizeChange = (newSize) => {
    setPageSize(newSize);
    setCurrentPage(1);
  };

  // フィルター変更時にページをリセット
  React.useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, groupFilter, visibilityFilter]);

  /* ===== モーダル関連 ===== */
  const closeConfirmModal = () => {
    setConfirmModal({
      isOpen: false,
      title: '',
      message: '',
      targetName: '',
      warning: '',
      confirmText: '',
      confirmButtonClass: 'btn-danger',
      onConfirm: null,
    });
  };

  /* ===== イベントハンドラ ===== */
  const handleResetFilters = () => {
    setSearchQuery('');
    setGroupFilter('');
    setVisibilityFilter('');
    setCurrentPage(1);
  };

  // 犬種グループ一覧を開く
  const handleOpenGroupList = () => {
    setIsGroupListOpen(true);
  };

  // 犬種グループ追加
  const handleAddGroup = () => {
    setEditingGroup(null);
    setIsGroupListOpen(false);
    setIsGroupFormOpen(true);
  };

  // 犬種グループ編集
  const handleEditGroup = (group) => {
    setEditingGroup(group);
    setIsGroupListOpen(false);
    setIsGroupFormOpen(true);
  };

  // 犬種追加
  const handleAddBreed = () => {
    setEditingBreed(null);
    setIsBreedFormOpen(true);
  };

  // 犬種編集
  const handleEditBreed = (breed) => {
    setEditingBreed(breed);
    setIsBreedFormOpen(true);
  };

  // グループ名を取得するヘルパー
  const getGroupLabel = (groupId) => {
    const group = dogGroups.find((g) => g.id === groupId);
    return group ? group.group_name : '—';
  };

  /* ===== レンダリング ===== */
  return (
    <div className="dm-root">
      <div className="dm-filterCard">
        <div className="dm-filterHeader">
          <div className="listTitle">犬種グループ・犬種管理</div>
          <div className="dm-filterHeaderRight">
            <button
              className="dm-addLink"
              onClick={handleOpenGroupList}
            >
              犬種グループ管理
            </button>
            <button
              className="dm-addLink"
              onClick={handleAddBreed}
            >
              ＋犬種・バラエティー追加
            </button>
          </div>
        </div>

        <div className="filterBar">
          <input
            className="searchInput"
            placeholder="犬種名・バラエティー名で検索"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />

          <div className="dmFilterSelectWrapper">
            <select
              className="dmFilterSelect"
              value={groupFilter}
              onChange={(e) => setGroupFilter(e.target.value)}
            >
              <option value="">すべてのグループ</option>
              {dogGroups.map((group) => (
                <option key={group.id} value={group.id}>
                  {group.group_name}
                </option>
              ))}
            </select>
          </div>

          <div className="dmFilterSelectWrapper">
            <select
              className="dmFilterSelect"
              value={visibilityFilter}
              onChange={(e) => setVisibilityFilter(e.target.value)}
            >
              <option value="">すべての表示状態</option>
              <option value="visible">表示のみ</option>
              <option value="hidden">非表示のみ</option>
            </select>
          </div>

          <button className="ghostButton" onClick={handleResetFilters}>
            条件リセット
          </button>

          {/* ページネーション */}
          <div className="dm-paginationGroup">
            <span className="dm-paginationLabel">表示件数:</span>
            <div className="dmFilterSelectWrapper dmFilterSelectWrapper--small">
              <select
                className="dmFilterSelect"
                value={pageSize}
                onChange={(e) => handlePageSizeChange(Number(e.target.value))}
              >
                {PAGE_SIZE_OPTIONS.map((size) => (
                  <option key={size} value={size}>{size}</option>
                ))}
              </select>
            </div>
            <span className="dm-paginationInfo">
              {totalItems > 0 ? `${startIndex + 1}-${endIndex} / ${totalItems}` : '0 of 0'}
            </span>
            <div className="dm-paginationButtons">
              <button
                className="dm-paginationBtn"
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
                title="最初のページ"
              >
                |&lt;
              </button>
              <button
                className="dm-paginationBtn"
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                title="前のページ"
              >
                &lt;
              </button>
              <button
                className="dm-paginationBtn"
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage >= totalPages}
                title="次のページ"
              >
                &gt;
              </button>
              <button
                className="dm-paginationBtn"
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage >= totalPages}
                title="最後のページ"
              >
                &gt;|
              </button>
            </div>
          </div>
        </div>
      </div>

      {isError && <div className="error-alert">{error.message}</div>}

      {isLoading && <div className="empty">読み込み中...</div>}

      {!isLoading && !isError && (
        <div className="dm-tableWrapper">
          <table className="dm-table">
            <thead>
              <tr>
                <th>犬種グループ</th>
                <th>犬種名</th>
                <th>バラエティー名</th>
                <th className="dm-th-center">表示</th>
                <th>登録日</th>
                <th>更新日</th>
                <th className="dm-th-center">操作</th>
              </tr>
            </thead>
            <tbody>
              {paginatedBreeds.length === 0 ? (
                <tr>
                  <td colSpan="7" className="dm-table-empty">
                    犬種データがありません
                  </td>
                </tr>
              ) : (
                paginatedBreeds.map((breed) => (
                  <tr key={breed.id} className="dm-table-row">
                    <td className="dm-group-cell" data-label="犬種グループ">{getGroupLabel(breed.group_id)}</td>
                    <td className="dm-breed-cell" data-label="犬種名">{breed.breed_name}</td>
                    <td className="dm-variety-cell" data-label="バラエティー名">{breed.variety_name || ''}</td>
                    <td className="dm-visibility-cell" data-label="表示">
                      <span className={`dm-visibility-icon ${breed.is_visible ? 'is-visible' : 'is-hidden'}`}>
                        {breed.is_visible ? '○' : ''}
                      </span>
                    </td>
                    <td className="dm-date-cell" data-label="登録日">{formatDateTime(breed.created_at)}</td>
                    <td className="dm-date-cell" data-label="更新日">{formatDateTime(breed.updated_at)}</td>
                    <td className="dm-actions-cell" data-label="操作">
                      <button className="btn btn-primary btn-sm" onClick={() => handleEditBreed(breed)}>
                        ✏️編集
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* 下部のアクションボタンエリア */}
      {/* <div className="dm-bottomActions">
        <button className="dm-addLink" onClick={handleOpenGroupList}>
          犬種グループ管理
        </button>
        <button className="dm-addLink" onClick={handleAddBreed}>
          ＋犬種・バラエティー追加
        </button>
      </div> */}

      {/* 犬種グループ一覧モーダル */}
      <DogGroupListModal
        isOpen={isGroupListOpen}
        onClose={() => setIsGroupListOpen(false)}
        dogGroups={dogGroups}
        onEdit={handleEditGroup}
        onAdd={handleAddGroup}
      />

      {/* 犬種グループ追加/編集モーダル */}
      <DogGroupFormModal
        isOpen={isGroupFormOpen}
        onClose={() => {
          setIsGroupFormOpen(false);
          setEditingGroup(null);
        }}
        mutation={saveMutation}
        dogGroup={editingGroup}
      />

      {/* 犬種・バラエティー追加/編集モーダル */}
      <DogBreedFormModal
        isOpen={isBreedFormOpen}
        onClose={() => {
          setIsBreedFormOpen(false);
          setEditingBreed(null);
        }}
        mutation={saveMutation}
        dogBreed={editingBreed}
        dogGroups={dogGroups}
      />

      {/* 確認モーダル */}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        onClose={closeConfirmModal}
        onConfirm={confirmModal.onConfirm}
        title={confirmModal.title}
        message={confirmModal.message}
        targetName={confirmModal.targetName}
        warning={confirmModal.warning}
        confirmText={confirmModal.confirmText}
        confirmButtonClass={confirmModal.confirmButtonClass}
      />
    </div>
  );
}